// src/contexts/DataContext.tsx
import React, { useEffect, useState, createContext, useContext, ReactNode, useCallback } from 'react';
import { withDirectoryFields } from '../lib/directoryProxy';
import { supabase, DB_SCHEMA } from '../supabase/config';

// Disable verbose logging in development
const DEBUG_LOGGING = false;
import { AllowanceTemplate, StaffAllowance, StaffSemesterData, DEFAULT_ALLOWANCE_TEMPLATES, normaliseFte } from '../types/staff';
import { UserRole } from '../types/roles';
import { DEFAULT_BASE_MINUTES_PER_FORTNIGHT } from '../types/schoolConfig';

// Staff interface that matches what StaffDirectory expects
// 
// TERMINOLOGY CLARIFICATION:
// - department: Job function within the college (Teaching, HR, Administration, etc.)
// - curriculumArea: Subject area for teaching staff (English, Mathematics, Science, HPE, etc.)
// - Curriculum Leaders manage teachers within their curriculum area
// - Heads of Department manage staff within their job function department
interface StaffMember {
  id: string;
  name: string;
  email: string;
  department: string; // Job function (Teaching, HR, Administration, etc.)
  curriculumArea?: string; // Subject area for teaching staff (English, Maths, Science, etc.)
  subjects: string[];
  baseMinutesPerFortnight: number; // Default DEFAULT_BASE_MINUTES_PER_FORTNIGHT for full-time
  fte: number;
  status: 'active' | 'leave' | 'inactive';
  role?: UserRole; // User role for permissions and access control
  // Staff code priority: manual staffCode > auto-generated initial
  staffCode?: string; // Manual 3-letter staff code (e.g., "ANV", "ALV") - takes priority
  initial?: string; // Auto-generated 3-letter teacher code like "AHA" - fallback
  fullName?: string; // Full name for display
  // ADD: Semester data
  semester1: StaffSemesterData;
  semester2: StaffSemesterData;
  both?: StaffSemesterData; // Support for 'both' semesters
  createdAt?: Date;
  updatedAt?: Date;
}

// Department interface - represents job functions within the college
interface Department {
  id: string;
  name: string; // Job function name (Teaching, HR, Administration, etc.)
  description?: string;
}
interface DataContextType {
  // Staff
  staff: StaffMember[];
  addStaffMember: (staffData: Omit<StaffMember, 'id'>) => Promise<string>;
  updateStaffMember: (id: string, staffData: Partial<StaffMember>) => Promise<void>;
  deleteStaffMember: (id: string) => Promise<void>;
  /**
   * Re-read the staff roster from the database.
   *
   * Needed by screens that write to `staff` directly rather than through
   * addStaffMember/updateStaffMember/deleteStaffMember above — currently only
   * User Management. Without it those writes are invisible to every consumer of
   * `staff` (staff directory, allowances, the intentions "fill on behalf"
   * picker) until a full page reload.
   */
  refreshStaff: () => Promise<void>;

  // Allowances
  allowanceTemplates: AllowanceTemplate[];
  addAllowance: (staffId: string, allowance: Omit<StaffAllowance, 'id'>, semester: 'semester1' | 'semester2' | 'both') => Promise<string>;
  updateAllowance: (staffId: string, allowanceId: string, updates: Partial<StaffAllowance>, semester: 'semester1' | 'semester2' | 'both') => Promise<void>;
  deleteAllowance: (staffId: string, allowanceId: string, semester: 'semester1' | 'semester2' | 'both') => Promise<void>;

  // Allowance Templates
  addAllowanceTemplate: (template: Omit<AllowanceTemplate, 'id'>) => Promise<string>;
  updateAllowanceTemplate: (id: string, updates: Partial<AllowanceTemplate>) => Promise<void>;
  deleteAllowanceTemplate: (id: string) => Promise<void>;

  // Departments
  departments: Department[];
  addDepartment: (deptData: Omit<Department, 'id'>) => Promise<string>;
  updateDepartment: (id: string, deptData: Partial<Department>) => Promise<void>;
  deleteDepartment: (id: string) => Promise<void>;

  // Loading states
  loading: boolean;
  error: string | null;

  // Staff lookup helpers
  getStaffDisplayCode: (staffMember: StaffMember) => string;
  findStaffByCode: (code: string) => StaffMember | undefined;
}
const DataContext = createContext<DataContextType | undefined>(undefined);
export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
interface DataProviderProps {
  children: ReactNode;
}
export const DataProvider: React.FC<DataProviderProps> = ({
  children
}) => {
  // For now, we'll make DataProvider work without requiring user authentication
  // This breaks the circular dependency while maintaining functionality
  const user = null; // We'll handle auth checking in individual components that need it
  
  // Using Supabase instead of Firebase/DatabaseContext
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [allowanceTemplates, setAllowanceTemplates] = useState<AllowanceTemplate[]>(DEFAULT_ALLOWANCE_TEMPLATES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to generate 2-letter teacher code (matching real staff directory format)
  const generateTeacherCode = (name: string, existingCodes: string[] = []): string => {
    const nameParts = name.trim().split(' ');
    if (nameParts.length < 2) {
      // Single name fallback: first 2 letters
      const baseCode = name.substring(0, 2).toUpperCase();
      return ensureUniqueCode(baseCode, existingCodes);
    }

    // Standard format: first initial + last initial
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    const baseCode = (firstName[0] + lastName[0]).toUpperCase();

    return ensureUniqueCode(baseCode, existingCodes);
  };

  // Helper function to ensure code uniqueness
  const ensureUniqueCode = (baseCode: string, existingCodes: string[]): string => {
    if (!existingCodes.includes(baseCode)) {
      return baseCode;
    }

    // Add number suffix for duplicates
    let counter = 2;
    let uniqueCode = baseCode + counter;
    while (existingCodes.includes(uniqueCode)) {
      counter++;
      uniqueCode = baseCode + counter;
    }

    return uniqueCode;
  };

  // Helper function to get staff display code
  const getStaffDisplayCode = (staffMember: StaffMember): string => {
    return staffMember.staffCode || 'UNK';
  };

  // Helper function to find staff by code
  const findStaffByCode = (code: string): StaffMember | undefined => {
    return staff.find(s => s.staffCode === code);
  };

  // Extract fetchStaff and fetchAllowanceTemplates as useCallbacks so they can be called from CRUD operations
  const fetchStaff = useCallback(async () => {
      try {
        const { data: rawStaff, error } = await supabase
          .from('staff')
          .select('*')
          .order('name');
          
        if (error) {
          console.error('DataContext: Error fetching staff:', error);
          setError(error.message);
          setLoading(false);
          return;
        }
        
        // pit#274: name/email/department from directory when VITE_DIRECTORY_READS=on.
        const data = await withDirectoryFields(rawStaff ?? []);
        DEBUG_LOGGING && console.log('DataContext: Staff data received, count:', data?.length || 0);
        // First pass: collect existing codes to ensure uniqueness
        const existingCodes: string[] = [];
        (data || []).forEach((item: any) => {
          if (item.staff_code) existingCodes.push(item.staff_code);
        });

        const staffData: StaffMember[] = (data || []).map(item => {
          // Create default semester data if not present
          const semester1: StaffSemesterData = item.semester1 || {
            allowances: [],
            baseMinutesPerFortnight: DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
            allocatedMinutes: 0,
            availableMinutes: item.fte ? Math.round(DEFAULT_BASE_MINUTES_PER_FORTNIGHT * item.fte) : DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
            utilization: 0
          };
          const semester2: StaffSemesterData = item.semester2 || {
            allowances: [],
            baseMinutesPerFortnight: DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
            allocatedMinutes: 0,
            availableMinutes: item.fte ? Math.round(DEFAULT_BASE_MINUTES_PER_FORTNIGHT * item.fte) : DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
            utilization: 0
          };

          // IMPORTANT: Also include the 'both' field if it exists
          const both: StaffSemesterData | undefined = item.both || undefined;

          // Convert legacy maxPeriods to baseMinutesPerFortnight if needed
          const baseMinutesPerFortnight = item.base_minutes_per_fortnight || (item.max_periods ? item.max_periods * 110 : DEFAULT_BASE_MINUTES_PER_FORTNIGHT);

          // Generate unique code if needed and track it
          const generatedCode = item.initial || generateTeacherCode(item.name || '', existingCodes);
          if (!item.initial && generatedCode) {
            existingCodes.push(generatedCode); // Add to prevent duplicates in this batch
          }

          const staffMember: StaffMember = {
            id: item.id,
            name: item.name || '',
            email: item.email || '',
            department: item.department || '',
            curriculumArea: item.curriculum_area,
            subjects: item.subjects || [],
            baseMinutesPerFortnight,
            fte: normaliseFte(item.fte),
            status: item.status || 'active',
            role: item.role as UserRole || 'teacher', // Read role from database, default to 'teacher'
            // Staff code from database
            staffCode: item.staff_code || generatedCode, // Use database staff_code or fallback to generated
            initial: item.staff_code || generatedCode, // Keep initial in sync with staffCode for compatibility
            fullName: item.name || '',
            // Add semester data
            semester1,
            semester2,
            // IMPORTANT: Include the 'both' field
            both,
            createdAt: item.created_at ? new Date(item.created_at) : undefined,
            updatedAt: item.updated_at ? new Date(item.updated_at) : undefined
          };
          return staffMember;
        });
        
        DEBUG_LOGGING && console.log('DataContext: Setting staff data:', staffData.length, 'members');
        setStaff(staffData);
        setLoading(false);
      } catch (err) {
        console.error('DataContext: Error fetching staff:', err);
        setError((err as Error).message);
        setLoading(false);
      }
    }, [generateTeacherCode]); // Dependencies for useCallback

  // Extract fetchAllowanceTemplates as useCallback for CRUD operations
  const fetchAllowanceTemplates = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('allowance_templates')
        .select('*')
        .order('name');

      if (error) {
        console.error('DataContext: Error fetching allowance templates:', error);
        setError(error.message);
        return;
      }

      DEBUG_LOGGING && console.log('DataContext: Allowance templates data received, count:', data?.length || 0);
      if (data && data.length > 0) {
        const templatesData: AllowanceTemplate[] = data.map(item => ({
          id: item.id,
          name: item.name || '',
          defaultMinutes: item.default_minutes || 0,
          description: item.description || '',
          // Carried through so the picker can hide retired templates. Absent
          // means active — never treat a missing flag as retired.
          isActive: item.is_active !== false
        }));

        DEBUG_LOGGING && console.log('DataContext: Setting allowance templates:', templatesData.length, 'templates');
        setAllowanceTemplates(templatesData);
      } else {
        // If no templates in Supabase yet, use defaults and create them
        DEBUG_LOGGING && console.log('DataContext: No allowance templates found, using defaults');
        // Create the default templates in Supabase
        for (const template of DEFAULT_ALLOWANCE_TEMPLATES) {
          try {
            const { error: insertError } = await supabase
              .from('allowance_templates')
              .insert({
                name: template.name,
                default_minutes: template.defaultMinutes,
                description: template.description || '',
                created_at: new Date().toISOString()
              });

            if (insertError) {
              console.error('Error creating default template:', insertError);
            }
          } catch (err) {
            console.error('Error creating default template:', err);
          }
        }
        // Set the defaults immediately
        setAllowanceTemplates(DEFAULT_ALLOWANCE_TEMPLATES);
      }
    } catch (err) {
      console.error('DataContext: Error fetching allowance templates:', err);
      setError((err as Error).message);
    }
  }, []); // No dependencies

  // Set up real-time listeners with Supabase
  // Check for authenticated session before fetching data
  useEffect(() => {
    DEBUG_LOGGING && console.log('DataContext: Setting up Supabase listeners...');

    // Check if user is authenticated before fetching data
    const initializeData = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        DEBUG_LOGGING && console.log('DataContext: No active session, skipping data fetch');
        setLoading(false);
        return;
      }

      DEBUG_LOGGING && console.log('DataContext: Active session found, fetching data...');
      fetchStaff();
      fetchDepartments();
      fetchAllowanceTemplates();
    };

    // Departments fetch function
    const fetchDepartments = async () => {
      try {
        const { data, error } = await supabase
          .from('departments')
          .select('*')
          .order('name');

        if (error) {
          console.error('DataContext: Error fetching departments:', error);
          setError(error.message);
          return;
        }

        DEBUG_LOGGING && console.log('DataContext: Departments data received, count:', data?.length || 0);
        const deptData: Department[] = (data || []).map(item => ({
          id: item.id,
          name: item.name || '',
          description: item.description || ''
        }));

        DEBUG_LOGGING && console.log('DataContext: Setting departments data:', deptData.length, 'departments');
        setDepartments(deptData);
      } catch (err) {
        console.error('DataContext: Error fetching departments:', err);
        setError((err as Error).message);
      }
    };

    initializeData();

    // Set up real-time subscription for staff.
    //
    // NOTE: this is still inert (issue #203): `staff` is not in the
    // `supabase_realtime` publication in any schema, so Postgres emits nothing
    // for it. The schema is now the app schema (DB_SCHEMA, directory migration
    // Step 0) rather than a hard-coded 'public', so it will fire if the table is
    // ever published. Screens that write to `staff` outside this context must
    // call refreshStaff() themselves.
    const staffSubscription = supabase
      .channel('staff-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: DB_SCHEMA,
          table: 'staff'
        },
        (payload) => {
          DEBUG_LOGGING && console.log('DataContext: Staff change detected:', payload);
          fetchStaff();
        }
      )
      .subscribe();

    // Set up real-time subscription for departments
    const departmentsSubscription = supabase
      .channel('departments-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: DB_SCHEMA,
          table: 'departments'
        },
        (payload) => {
          DEBUG_LOGGING && console.log('DataContext: Departments change detected:', payload);
          fetchDepartments();
        }
      )
      .subscribe();
    
    // Set up real-time subscription for allowance templates
    const templatesSubscription = supabase
      .channel('allowance-templates-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: DB_SCHEMA,
          table: 'allowance_templates'
        },
        (payload) => {
          DEBUG_LOGGING && console.log('DataContext: Allowance templates change detected:', payload);
          fetchAllowanceTemplates();
        }
      )
      .subscribe();

    // Listen for auth state changes to re-fetch data on sign in
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      DEBUG_LOGGING && console.log('DataContext: Auth state changed:', event);

      if (event === 'SIGNED_IN' && session) {
        DEBUG_LOGGING && console.log('DataContext: User signed in, fetching data...');
        fetchStaff();
        fetchDepartments();
        fetchAllowanceTemplates();
      } else if (event === 'SIGNED_OUT') {
        DEBUG_LOGGING && console.log('DataContext: User signed out, clearing data...');
        setStaff([]);
        setDepartments([]);
        setAllowanceTemplates([]);
        setError(null);
        setLoading(false);
      }
    });

    // Cleanup listeners on unmount
    return () => {
      console.log('DataContext: Cleaning up listeners');
      staffSubscription.unsubscribe();
      departmentsSubscription.unsubscribe();
      templatesSubscription.unsubscribe();
      authListener.subscription.unsubscribe();
    };
  }, []); // No dependencies since we're using Supabase

  // Staff CRUD operations
  const addStaffMember = async (staffData: Omit<StaffMember, 'id'>): Promise<string> => {
    try {
      console.log('DataContext: Adding staff member:', staffData);

      // Generate teacher code if not provided
      const existingCodes = staff.map(s => s.staffCode || s.initial).filter(Boolean);
      const initial = staffData.initial || generateTeacherCode(staffData.name, existingCodes);

      // Ensure semester data exists
      const semester1: StaffSemesterData = staffData.semester1 || {
        allowances: [],
        baseMinutesPerFortnight: staffData.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
        allocatedMinutes: 0,
        availableMinutes: Math.round((staffData.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT) * staffData.fte),
        utilization: 0
      };
      const semester2: StaffSemesterData = staffData.semester2 || {
        allowances: [],
        baseMinutesPerFortnight: staffData.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
        allocatedMinutes: 0,
        availableMinutes: Math.round((staffData.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT) * staffData.fte),
        utilization: 0
      };

      // Generate unique staff_id if not provided (required by database)
      const staffId = staffData.staffCode || initial || `STAFF_${Date.now()}`;

      // Only insert fields that exist in the database schema
      // The staff table in Supabase has these fields based on schema.sql:
      // id, staff_id, name, email, department, role, curriculum_area, employment_type, fte, status, permissions, created_at, updated_at
      const insertData: any = {
        staff_id: staffId,
        name: staffData.name,
        // Normalize email to lowercase for case-insensitive SSO matching
        email: staffData.email ? staffData.email.toLowerCase().trim() : null,
        fte: normaliseFte(staffData.fte),
        status: staffData.status || 'active',
        role: staffData.role || 'teacher' // Add role field with default
      };

      // Add optional fields if they exist
      if (staffData.department) insertData.department = staffData.department;
      if (staffData.curriculumArea) insertData.curriculum_area = staffData.curriculumArea;
      if (staffData.staffCode) insertData.staff_code = staffData.staffCode;
      if (staffData.subjects) insertData.subjects = staffData.subjects;
      if (staffData.baseMinutesPerFortnight) insertData.base_minutes_per_fortnight = staffData.baseMinutesPerFortnight;

      const { data, error } = await supabase
        .from('staff')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('DataContext: Error adding staff member:', error);
        throw error;
      }

      console.log('DataContext: Staff member added with ID:', data.id);

      // Manually refresh staff data to update UI immediately
      await fetchStaff();

      return data.id;
    } catch (err) {
      console.error('DataContext: Error adding staff member:', err);
      throw err;
    }
  };
  const updateStaffMember = async (id: string, staffData: Partial<StaffMember>): Promise<void> => {
    try {
      console.log('DataContext: Updating staff member:', id, staffData);
      const updateData: any = {
        updated_at: new Date().toISOString()
      };

      // Map camelCase to snake_case for Supabase
      if (staffData.name !== undefined) updateData.name = staffData.name;
      if (staffData.email !== undefined) {
        // Normalize email to lowercase for case-insensitive SSO matching
        updateData.email = staffData.email ? staffData.email.toLowerCase().trim() : null;
      }
      if (staffData.department !== undefined) updateData.department = staffData.department;
      if (staffData.curriculumArea !== undefined) updateData.curriculum_area = staffData.curriculumArea;
      if (staffData.subjects !== undefined) updateData.subjects = staffData.subjects;
      if (staffData.baseMinutesPerFortnight !== undefined) updateData.base_minutes_per_fortnight = staffData.baseMinutesPerFortnight;
      if (staffData.fte !== undefined) updateData.fte = staffData.fte;
      if (staffData.status !== undefined) updateData.status = staffData.status;
      if (staffData.role !== undefined) updateData.role = staffData.role;
      if (staffData.staffCode !== undefined) updateData.staff_code = staffData.staffCode;
      if (staffData.semester1 !== undefined) updateData.semester1 = staffData.semester1;
      if (staffData.semester2 !== undefined) updateData.semester2 = staffData.semester2;
      if (staffData.both !== undefined) updateData.both = staffData.both;

      // NOTE: 'initial' column doesn't exist in the database - only 'staff_code' exists
      // If you need to track teacher codes, use staff_code field instead
      // Update initial code if name changed
      // if (staffData.name && !staffData.initial) {
      //   const existingCodes = staff.map(s => s.staffCode || s.initial).filter(Boolean);
      //   updateData.initial = generateTeacherCode(staffData.name, existingCodes);
      // } else if (staffData.initial !== undefined) {
      //   updateData.initial = staffData.initial;
      // }

      console.log('DataContext: Update data being sent:', updateData);

      // .select() so we can tell "updated" from "matched nothing".
      //
      // Without it this is a silent-failure machine: when RLS filters the row
      // out, PostgREST returns error: null and zero rows, the code below reports
      // "updated successfully", and the change simply never happened. Sarah hit
      // exactly this — a save that reported success and did not stick, with no
      // error to go on.
      //
      // pit.staff is guarded by staff_admin_update / staff_self_update plus the
      // RESTRICTIVE tenant_isolation and zz_require_pit_membership policies, so
      // there are several ways for a legitimate-looking update to match no rows:
      // pit.current_tenant() resolving to NULL, pit.is_admin() failing because
      // auth_user_id is unset, or editing someone else's row without admin.
      const { data: updatedRows, error } = await supabase
        .from('staff')
        .update(updateData)
        .eq('id', id)
        .select('id, name, email, department, curriculum_area, base_minutes_per_fortnight, fte, status, role, staff_code');

      if (error) {
        console.error('DataContext: Error updating staff member:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          fullError: error
        });
        throw error;
      }

      // Verify the scalar fields we sent actually took.
      //
      // Sarah reported an FTE edit that did not stick, and the logs bear it out:
      // four PATCHes to one staff row in an hour with the value unchanged. The
      // row DID match — updated_at moved each time — so the zero-row check below
      // would not have caught it, and every remote explanation I could test came
      // back negative (no trigger touches fte, it is not generated, there is no
      // sync job, it is nullable with zero nulls).
      //
      // Rather than guess again, make the next occurrence name itself: compare
      // what came back against what was sent and say which field refused.
      // Scalars only — jsonb (semester1/2/both) needs a deep compare and is not
      // where the problem showed up.
      const SCALARS = ['name', 'email', 'department', 'curriculum_area',
                       'base_minutes_per_fortnight', 'fte', 'status', 'role', 'staff_code'] as const;
      if (updatedRows && updatedRows.length > 0) {
        const saved = updatedRows[0] as Record<string, unknown>;
        // Compare by value, not by type. Postgres `numeric` can come back as
        // the string "0.90" for a sent number 0.9, and a strict !== would then
        // throw on every successful FTE save — turning a diagnostic into an
        // outage. Normalise anything numeric-looking before comparing.
        const differs = (a: unknown, b: unknown): boolean => {
          if (a === b) return false;
          const na = Number(a);
          const nb = Number(b);
          if (Number.isFinite(na) && Number.isFinite(nb)) return na !== nb;
          return String(a ?? '') !== String(b ?? '');
        };
        const rejected = SCALARS.filter(
          col => col in updateData && saved[col] !== undefined && differs(saved[col], updateData[col])
        );
        if (rejected.length > 0) {
          console.error('DataContext: fields did not persist', {
            id, rejected,
            sent: Object.fromEntries(rejected.map(c => [c, updateData[c]])),
            saved: Object.fromEntries(rejected.map(c => [c, saved[c]]))
          });
          throw new Error(
            `The change was not saved — the database kept its existing value for: ${rejected.join(', ')}. ` +
            `Please report this with the staff member's name and which field you changed.`
          );
        }
      }

      if (!updatedRows || updatedRows.length === 0) {
        // Report it rather than claiming success. The user gets something they
        // can act on instead of a change that quietly vanishes on refresh.
        throw new Error(
          'The change was not saved — the database accepted the request but updated no rows. ' +
          'This usually means your account lacks permission to edit that staff member. ' +
          'Please report it with the staff member’s name.'
        );
      }

      console.log('DataContext: Staff member updated successfully');

      // Manually refresh staff data to update UI immediately
      // This ensures UI updates even if realtime subscription doesn't fire
      await fetchStaff();
    } catch (err) {
      console.error('DataContext: Error updating staff member:', {
        message: (err as any).message,
        details: (err as any).details,
        hint: (err as any).hint,
        code: (err as any).code,
        fullError: err
      });
      throw err;
    }
  };
  const deleteStaffMember = async (id: string): Promise<void> => {
    try {
      console.log('DataContext: Deleting staff member:', id);

      const { error } = await supabase
        .from('staff')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('DataContext: Error deleting staff member:', error);
        throw error;
      }

      console.log('DataContext: Staff member deleted');

      // Manually refresh staff data to update UI immediately
      await fetchStaff();
    } catch (err) {
      console.error('DataContext: Error deleting staff member:', err);
      throw err;
    }
  };

  // Allowance CRUD operations
  const addAllowance = async (staffId: string, allowance: Omit<StaffAllowance, 'id'>, semester: 'semester1' | 'semester2' | 'both'): Promise<string> => {
    try {
      console.log('DataContext: Adding allowance for staff:', staffId, allowance, semester);

      // Get current staff member
      const staffMember = staff.find(s => s.id === staffId);
      if (!staffMember) {
        throw new Error(`Staff member with ID ${staffId} not found`);
      }

      // Create new allowance with ID
      const newAllowance: StaffAllowance = {
        ...allowance,
        id: `allowance_${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Handle the case where the allowance applies to both semesters
      if (semester === 'both') {
        console.log('DataContext: Allowance applies to both semesters');

        // Get the "both" data or initialize it
        const bothData = staffMember.both || {
          allowances: [],
          baseMinutesPerFortnight: staffMember.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
          allocatedMinutes: 0,
          availableMinutes: Math.round((staffMember.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT) * (staffMember.fte || 1.0)),
          utilization: 0
        };

        // Make sure allowances is an array
        const currentAllowances = Array.isArray(bothData.allowances) ? bothData.allowances : [];

        // Update the both data
        const updatedBothData = {
          ...bothData,
          allowances: [...currentAllowances, newAllowance]
        };

        // Update staff document in Supabase
        const { error } = await supabase
          .from('staff')
          .update({
            both: updatedBothData,
            updated_at: new Date().toISOString()
          })
          .eq('id', staffId);

        if (error) throw error;
      } else {
        // Get the current semester data or initialize it if it doesn't exist
        const currentSemesterData = staffMember[semester] || {
          allowances: [],
          baseMinutesPerFortnight: staffMember.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
          allocatedMinutes: 0,
          availableMinutes: Math.round((staffMember.baseMinutesPerFortnight || DEFAULT_BASE_MINUTES_PER_FORTNIGHT) * (staffMember.fte || 1.0)),
          utilization: 0
        };

        // Make sure allowances is an array
        const currentAllowances = Array.isArray(currentSemesterData.allowances) ? currentSemesterData.allowances : [];

        // Update the appropriate semester's allowances
        const updatedSemesterData = {
          ...currentSemesterData,
          allowances: [...currentAllowances, newAllowance]
        };

        // Update staff document in Supabase
        const { error } = await supabase
          .from('staff')
          .update({
            [semester]: updatedSemesterData,
            updated_at: new Date().toISOString()
          })
          .eq('id', staffId);

        if (error) throw error;
      }
      console.log('DataContext: Allowance added successfully with ID:', newAllowance.id);

      // Manually refresh staff data to update UI immediately
      await fetchStaff();

      // Return the ID of the newly created allowance
      return newAllowance.id;
    } catch (err) {
      console.error('DataContext: Error adding allowance:', err);
      throw err;
    }
  };
  const updateAllowance = async (staffId: string, allowanceId: string, updates: Partial<StaffAllowance>, semester: 'semester1' | 'semester2' | 'both'): Promise<void> => {
    try {
      console.log('DataContext: Updating allowance:', staffId, allowanceId, updates, semester);

      // Get current staff member
      const staffMember = staff.find(s => s.id === staffId);
      if (!staffMember) {
        throw new Error(`Staff member with ID ${staffId} not found`);
      }
      if (semester === 'both') {
        // Find and update the allowance in the both field
        if (!staffMember.both || !Array.isArray(staffMember.both.allowances)) {
          throw new Error(`Staff member ${staffId} has no 'both' allowances`);
        }
        const updatedAllowances = staffMember.both.allowances.map(allowance => {
          if (allowance.id === allowanceId) {
            return {
              ...allowance,
              ...updates,
              updatedAt: new Date()
            };
          }
          return allowance;
        });

        // Update the both allowances
        const updatedBothData = {
          ...staffMember.both,
          allowances: updatedAllowances
        };

        // Update staff document in Supabase
        const { error } = await supabase
          .from('staff')
          .update({
            both: updatedBothData,
            updated_at: new Date().toISOString()
          })
          .eq('id', staffId);

        if (error) throw error;
      } else {
        // Check if the semester data exists
        if (!staffMember[semester] || !Array.isArray(staffMember[semester].allowances)) {
          throw new Error(`Staff member ${staffId} has no ${semester} allowances`);
        }

        // Find and update the allowance
        const updatedAllowances = staffMember[semester].allowances.map(allowance => {
          if (allowance.id === allowanceId) {
            return {
              ...allowance,
              ...updates,
              updatedAt: new Date()
            };
          }
          return allowance;
        });

        // Update the appropriate semester's allowances
        const updatedSemesterData = {
          ...staffMember[semester],
          allowances: updatedAllowances
        };

        // Update staff document in Supabase
        const { error } = await supabase
          .from('staff')
          .update({
            [semester]: updatedSemesterData,
            updated_at: new Date().toISOString()
          })
          .eq('id', staffId);

        if (error) throw error;
      }
      console.log('DataContext: Allowance updated successfully');

      // Manually refresh staff data to update UI immediately
      await fetchStaff();
    } catch (err) {
      console.error('DataContext: Error updating allowance:', err);
      throw err;
    }
  };
  const deleteAllowance = async (staffId: string, allowanceId: string, semester: 'semester1' | 'semester2' | 'both'): Promise<void> => {
    try {
      console.log('DataContext: Deleting allowance:', staffId, allowanceId, semester);

      // Get current staff member
      const staffMember = staff.find(s => s.id === staffId);
      if (!staffMember) {
        throw new Error(`Staff member with ID ${staffId} not found`);
      }
      if (semester === 'both') {
        // Filter out the allowance to delete from both
        if (!staffMember.both || !Array.isArray(staffMember.both.allowances)) {
          throw new Error(`Staff member ${staffId} has no 'both' allowances`);
        }
        const updatedAllowances = staffMember.both.allowances.filter(allowance => allowance.id !== allowanceId);

        // Update the both allowances
        const updatedBothData = {
          ...staffMember.both,
          allowances: updatedAllowances
        };

        // Update staff document in Supabase
        const { error } = await supabase
          .from('staff')
          .update({
            both: updatedBothData,
            updated_at: new Date().toISOString()
          })
          .eq('id', staffId);

        if (error) throw error;
      } else {
        // Check if the semester data exists
        if (!staffMember[semester] || !Array.isArray(staffMember[semester].allowances)) {
          throw new Error(`Staff member ${staffId} has no ${semester} allowances`);
        }

        // Filter out the allowance to delete
        const updatedAllowances = staffMember[semester].allowances.filter(allowance => allowance.id !== allowanceId);

        // Update the appropriate semester's allowances
        const updatedSemesterData = {
          ...staffMember[semester],
          allowances: updatedAllowances
        };

        // Update staff document in Supabase
        const { error } = await supabase
          .from('staff')
          .update({
            [semester]: updatedSemesterData,
            updated_at: new Date().toISOString()
          })
          .eq('id', staffId);

        if (error) throw error;
      }
      console.log('DataContext: Allowance deleted successfully');

      // Manually refresh staff data to update UI immediately
      await fetchStaff();
    } catch (err) {
      console.error('DataContext: Error deleting allowance:', err);
      throw err;
    }
  };

  // Allowance Template CRUD operations
  const addAllowanceTemplate = async (template: Omit<AllowanceTemplate, 'id'>): Promise<string> => {
    try {
      console.log('DataContext: Adding allowance template:', template);
      const { data, error } = await supabase
        .from('allowance_templates')
        .insert({
          name: template.name,
          default_minutes: template.defaultMinutes,
          description: template.description,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      console.log('DataContext: Allowance template added with ID:', data.id);

      // Manually refresh templates to update UI immediately
      await fetchAllowanceTemplates();

      return data.id;
    } catch (err) {
      console.error('DataContext: Error adding allowance template:', err);
      throw err;
    }
  };
  const updateAllowanceTemplate = async (id: string, updates: Partial<AllowanceTemplate>): Promise<void> => {
    try {
      console.log('DataContext: Updating allowance template:', id, updates);
      const updateData: any = {
        updated_at: new Date().toISOString()
      };
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.defaultMinutes !== undefined) updateData.default_minutes = updates.defaultMinutes;
      if (updates.description !== undefined) updateData.description = updates.description;

      const { error } = await supabase
        .from('allowance_templates')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      console.log('DataContext: Allowance template updated');

      // Manually refresh templates to update UI immediately
      await fetchAllowanceTemplates();
    } catch (err) {
      console.error('DataContext: Error updating allowance template:', err);
      throw err;
    }
  };
  const deleteAllowanceTemplate = async (id: string): Promise<void> => {
    try {
      console.log('DataContext: Deleting allowance template:', id);
      const { error } = await supabase
        .from('allowance_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      console.log('DataContext: Allowance template deleted');

      // Manually refresh templates to update UI immediately
      await fetchAllowanceTemplates();
    } catch (err) {
      console.error('DataContext: Error deleting allowance template:', err);
      throw err;
    }
  };

  // Department CRUD operations
  const addDepartment = async (deptData: Omit<Department, 'id'>): Promise<string> => {
    try {
      console.log('DataContext: Adding department:', deptData);
      const { data, error } = await supabase
        .from('departments')
        .insert({
          name: deptData.name,
          description: deptData.description
        })
        .select()
        .single();

      if (error) throw error;

      console.log('DataContext: Department added with ID:', data.id);
      return data.id;
    } catch (err) {
      console.error('DataContext: Error adding department:', err);
      throw err;
    }
  };
  const updateDepartment = async (id: string, deptData: Partial<Department>): Promise<void> => {
    try {
      console.log('DataContext: Updating department:', id, deptData);
      const { error } = await supabase
        .from('departments')
        .update(deptData)
        .eq('id', id);

      if (error) throw error;

      console.log('DataContext: Department updated');
    } catch (err) {
      console.error('DataContext: Error updating department:', err);
      throw err;
    }
  };
  const deleteDepartment = async (id: string): Promise<void> => {
    try {
      console.log('DataContext: Deleting department:', id);
      const { error } = await supabase
        .from('departments')
        .delete()
        .eq('id', id);

      if (error) throw error;

      console.log('DataContext: Department deleted');
    } catch (err) {
      console.error('DataContext: Error deleting department:', err);
      throw err;
    }
  };
  const value: DataContextType = {
    // Data
    staff,
    departments,
    allowanceTemplates,
    // Staff operations
    addStaffMember,
    updateStaffMember,
    deleteStaffMember,
    refreshStaff: fetchStaff,
    // Allowance operations
    addAllowance,
    updateAllowance,
    deleteAllowance,
    // Allowance Template operations
    addAllowanceTemplate,
    updateAllowanceTemplate,
    deleteAllowanceTemplate,
    // Department operations
    addDepartment,
    updateDepartment,
    deleteDepartment,
    // State
    loading,
    error,
    // Staff lookup helpers
    getStaffDisplayCode,
    findStaffByCode
  };
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};